"use client";

import React, { useState, useEffect } from 'react';
import FirebaseService from '@/lib/firebase';
import type { Assistant } from '@/lib/firebase';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ExternalLink, Settings, Loader2 } from 'lucide-react';
import { CategorizedToolPreview } from '@/components/creator/CategorizedToolPreview';

interface AssistantLinkPreviewProps {
  assistantId: string;
  className?: string;
}

export function AssistantLinkPreview({ assistantId, className }: AssistantLinkPreviewProps) {
  const [assistant, setAssistant] = useState<Assistant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAssistant = async () => {
      try {
        setLoading(true);
        setError(null);
        const assistantData = await FirebaseService.getAssistant(assistantId);
        console.log('AssistantLinkPreview - loaded assistant:', assistantId, 'avatar:', assistantData?.avatar);
        setAssistant(assistantData);
      } catch (err) {
        console.error('Error fetching assistant preview:', err);
        setError('Failed to load assistant');
      } finally {
        setLoading(false);
      }
    };

    if (assistantId) {
      fetchAssistant();
    }
  }, [assistantId]);

  if (loading) {
    return (
      <Card className={`w-full max-w-md ${className}`}>
        <CardContent className="flex items-center justify-center p-4">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">Loading assistant...</span>
        </CardContent>
      </Card>
    );
  }

  if (error || !assistant) {
    return (
      <Card className={`w-full max-w-md border-destructive/20 ${className}`}>
        <CardContent className="p-4">
          <p className="text-sm text-destructive">
            {error || 'Assistant not found'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const handleOpenAssistant = () => {
    window.open(`/assistant/${assistantId}`, '_blank');
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <button
      onClick={handleOpenAssistant}
      className={`w-full text-left group ${className}`}
    >
      <Card className="hover:bg-muted/50 hover:border-primary/20 transition-all cursor-pointer">
        <CardContent className="p-2">
          <div className="flex items-center gap-2">
            <div className="relative h-8 w-8 flex-shrink-0 rounded overflow-hidden bg-muted flex items-center justify-center">
              {assistant.avatar ? (
                <img
                  src={assistant.avatar}
                  alt={assistant.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="absolute inset-0 flex items-center justify-center text-[10px] font-medium" style={{ display: assistant.avatar ? 'none' : 'flex' }}>
                {assistant.name.slice(0, 2).toUpperCase()}
              </div>
            </div>

            <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-xs truncate group-hover:text-primary transition-colors">
                  {assistant.name}
                </h3>
                <p className="text-[10px] text-muted-foreground">
                  {formatTimestamp(assistant.createdAt)}
                </p>
              </div>
              <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>
    </button>
  );
}